import React, { useEffect, useRef } from "react";
import { Box, Grid2, Button, Tooltip, Typography } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { TastePotentialGraph } from "./graphs/TastePotentialGraph/TastePotentialGraph";
import { ComparativeGraph } from "./graphs/ComparativeGraph/ComparativeGraph";
import { AromaPotentialGraph } from "./graphs/AromaPotentialGraph/AromaPotentialGraph";
import { BarGraph } from "./graphs/BarGraph/BarGraph";
import { LineGraph } from "./graphs/LineGraph/LineGraph";
import { LineStepGraph } from "./graphs/LineGraph/LineStepGraph";
import BarGraphTable from "../../detailpage/BarGraphTable";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPuzzlePiece } from "@fortawesome/free-solid-svg-icons";

export const ProductDetailGraphArea = ({
  dataAnalysisInfo,
  dataAvgAnalysisInfo,
  dataProfileInfo,
  dataProfileTempInfo,
}) => {
  const theme = createTheme({
    components: {
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            maxWidth: "none",
            backgroundColor: "transparent",
            color: "white",
          },
          popper: {
            width: "50%",
          },
          arrow: {
            color: "#EFF2F4",
          },
        },
      },
    },
  });
  const textCanvas = useRef();
  let ctx = null;

  // TastePotentialGraph, ComparativeGraph 적용
  // API에서 전달받은 데이터 중 1 이상을 넘는 값음 10으로 나누어 표시함
  const computeDataAnalysisInfo = { ...dataAnalysisInfo };
  for (let key in computeDataAnalysisInfo) {
    if (typeof computeDataAnalysisInfo[key] === "number") {
      computeDataAnalysisInfo[key] =
        computeDataAnalysisInfo[key] > 1
          ? computeDataAnalysisInfo[key] / 10
          : computeDataAnalysisInfo[key];
    }
  }
  const computeDataAvgAnalysisInfo = { ...dataAvgAnalysisInfo };
  for (let key in computeDataAvgAnalysisInfo) {
    if (typeof computeDataAvgAnalysisInfo[key] === "number") {
      computeDataAvgAnalysisInfo[key] =
        computeDataAvgAnalysisInfo[key] > 1
          ? computeDataAvgAnalysisInfo[key] / 10
          : computeDataAvgAnalysisInfo[key];
    }
  }

  // Draw Title
  // initialize the canvas context
  useEffect(() => {
    try {
      // dynamically assign the width and height to canvas
      const canvasEle = textCanvas.current;
      canvasEle.width = canvasEle.clientWidth;
      canvasEle.height = canvasEle.clientHeight;

      // get context of the canvas
      ctx = canvasEle.getContext("2d");

      ctx.beginPath();
      ctx.font = `bold 18px Microsoft YaHei`;
      ctx.fillStyle = "#333";
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText("Base Profile", 4, 0);
      ctx.stroke();
    } catch (e) {
      console.log("ctx", e);
    }
  }, []);

  return (
    <Box sx={{ mt: 8 }}>
      <Grid2 container spacing={5}>
        <Grid2 size={{ xs: 12, sm: 12, md: 4 }} sx={{ backgroundColor: "" }}>
          <TastePotentialGraph
            acidityPotential={computeDataAnalysisInfo.AcidityPotential}
            sweetnessPotential={computeDataAnalysisInfo.SweetnessPotential}
            aromaPotential={computeDataAnalysisInfo.AromaPotential}
            bodyPotential={computeDataAnalysisInfo.BodyPotential}
            bitternessPotential={computeDataAnalysisInfo.BitternessPotential}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 12, md: 4 }}>
          <ComparativeGraph
            acidityPotential={computeDataAnalysisInfo.AcidityPotential}
            sweetnessPotential={computeDataAnalysisInfo.SweetnessPotential}
            aromaPotential={computeDataAnalysisInfo.AromaPotential}
            bodyPotential={computeDataAnalysisInfo.BodyPotential}
            bitternessPotential={computeDataAnalysisInfo.BitternessPotential}
            dataAvgAnalysisInfo={computeDataAvgAnalysisInfo}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 12, md: 4 }}>
          <AromaPotentialGraph
            dataAnalysisInfo={dataAnalysisInfo}
            chlorogenicAcids={dataAnalysisInfo.ChlorogenicAcids}
            sucrose={dataAnalysisInfo.Sucrose}
            monoSugar={dataAnalysisInfo.MonoSugar}
            diterpene={dataAnalysisInfo.Diterpene}
            trigonelline={dataAnalysisInfo.Trigonelline}
            organicAcids={dataAnalysisInfo.OrganicAcids}
          />
        </Grid2>
      </Grid2>

      <Grid2
        container
        sx={{
          position: "relative",
        }}
      >
        <Box
          size={12}
          sx={{ width: "100%", height: "100%", padding: "0px", margin: "0px" }}
        >
          <BarGraph dataAnalysisInfo={dataAnalysisInfo} />
        </Box>

        <Box
          size={12}
          sx={{ width: "100%", height: "100%", padding: "0px", margin: "0px" }}
        >
          <Typography
            component="h4"
            variant="h4"
            sx={{ padding: "2px 0", m: 0 }}
          >
            <FontAwesomeIcon
              icon={faPuzzlePiece}
              style={{ paddingLeft: "10px", paddingRight: "10px" }}
            />
            Base Profile
          </Typography>

          <Box
            size={12}
            sx={{
              width: "100%",
              height: "100%",
              padding: "0px",
              margin: "0px",
            }}
          >
            <LineGraph dataProfileTempInfo={dataProfileTempInfo} />
          </Box>

          <Box
            size={12}
            sx={{
              width: "100%",
              height: "100%",
              padding: "0px",
              margin: "0px",
            }}
          >
            <LineStepGraph dataProfileInfo={dataProfileInfo} />
          </Box>
        </Box>

        <ThemeProvider theme={theme}>
          <Tooltip
            title={<BarGraphTable />}
            arrow
            placement="left-end"
            sx={{
              position: "absolute",
              top: "0",
              right: "5%",
            }}
            PopperProps={{
              modifiers: [
                {
                  name: "flip",
                  enabled: true,
                  options: {
                    altBoundary: true,
                    rootBoundary: "viewport",
                  },
                },
                {
                  name: "preventOverflow",
                  options: {
                    altAxis: true,
                    altBoundary: true,
                    tether: true,
                    rootBoundary: "document",
                  },
                },
              ],
            }}
          >
            <IconButton>
              <InfoOutlinedIcon fontSize="large" sx={{ color: "#A2A2A2" }} />
            </IconButton>
          </Tooltip>
        </ThemeProvider>
      </Grid2>
    </Box>
  );
};
